import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

const LIFE_IMG =
  "https://images.pexels.com/photos/6873928/pexels-photo-6873928.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Diary() {
  const { t, lang } = useLang();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [genLoading, setGenLoading] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const [s, i, snap] = await Promise.all([
      api.get("/dashboard/summary"),
      api.get("/items"),
      api.get("/diary/snapshots"),
    ]);
    setSummary(s.data);
    setItems(i.data.items);
    setSnapshots(snap.data.snapshots);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (iso) =>
    new Date(iso).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  const fmtLong = (iso) =>
    new Date(iso).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const doneItems = items.filter((it) => it.status === "done");
  const inProgress = items.filter((it) => it.status === "in_progress");

  const generate = async (period) => {
    setGenLoading(period);
    setError("");
    try {
      const { data } = await api.post("/diary/snapshots", { period });
      setSnapshots((s) => [data.snapshot, ...s]);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenLoading(null);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-6xl">
      <p className="label-cap">— {t("app.sidebar.diary")}</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="diary-title">
        {t("app.diaryHeader")}
      </h1>
      <p className="font-display-italic text-[var(--ink-soft)] mt-4 max-w-3xl text-lg leading-relaxed">
        {t("app.diaryIntro")}
      </p>

      <div className="grid md:grid-cols-12 gap-12 mt-16">
        <section className="md:col-span-8 space-y-12">
          <div>
            <p className="label-cap text-[var(--oxblood)]">— {t("app.this_week_summary")}</p>
            <h2 className="font-display text-3xl mt-3 font-light tracking-tight">
              {summary?.this_week?.done ?? 0} {lang === "pt" ? "itens tratados, " : "items handled, "}
              <span className="font-display-italic">
                {summary?.this_week?.hours_saved ?? 0}h {lang === "pt" ? "recuperadas" : "recovered"}
              </span>.
            </h2>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => generate("weekly")}
                disabled={genLoading !== null}
                className="btn-outline"
                data-testid="snapshot-weekly-btn"
              >
                {genLoading === "weekly" ? t("app.snapshotGenerating") : `${t("app.snapshotCreate")} · ${t("app.snapshotWeekly")}`}
              </button>
              <button
                onClick={() => generate("monthly")}
                disabled={genLoading !== null}
                className="btn-ghost"
                data-testid="snapshot-monthly-btn"
              >
                {genLoading === "monthly" ? t("app.snapshotGenerating") : `${t("app.snapshotCreate")} · ${t("app.snapshotMonthly")}`}
              </button>
            </div>
            {error && <p className="text-[var(--error)] text-sm mt-3" data-testid="snapshot-error">{error}</p>}
          </div>

          {snapshots.length > 0 && (
            <div data-testid="snapshots-list">
              <p className="label-cap mb-4">— {t("app.snapshotHistory")}</p>
              <div className="space-y-px bg-[var(--border)]">
                {snapshots.map((s) => (
                  <article key={s.id} className="bg-[var(--bg)] p-8" data-testid={`snapshot-${s.id}`}>
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <p className="label-cap text-[var(--oxblood)]">
                        {s.period === "weekly" ? t("app.snapshotWeekly") : t("app.snapshotMonthly")}
                      </p>
                      <p className="text-xs text-[var(--ink-faint)] tracking-monocle uppercase">
                        {fmtLong(s.created_at)}
                      </p>
                    </div>
                    <p className="font-display text-lg mt-4 leading-snug">
                      {s.items_done} {lang === "pt" ? "itens · " : "items · "}
                      <span className="font-display-italic">{s.hours_saved}h {lang === "pt" ? "recuperadas" : "recovered"}</span>
                      {s.memories_archived > 0 && (
                        <span data-testid={`snapshot-memory-${s.id}`}>
                          {" · "}
                          {s.memories_archived} {lang === "pt" ? "memória(s) arquivada(s)" : "memory item(s) archived"}
                          {s.memory_spend_total != null && (
                            <span className="font-display-italic">
                              {" · "}
                              {s.memory_spend_total.toFixed(2)} {s.memory_spend_currency || ""}
                            </span>
                          )}
                        </span>
                      )}
                    </p>
                    <p className="mt-4 text-[15px] leading-relaxed text-[var(--ink)] whitespace-pre-line">
                      {s.narrative}
                    </p>
                    {s.agreement_digest?.items?.length > 0 && (
                      <div className="mt-5 pt-4 border-t border-[var(--border)]" data-testid={`snapshot-agreements-${s.id}`}>
                        <p className="label-cap text-[var(--oxblood)]">— {lang === "pt" ? "Acordos digitais" : "Digital pacts"}</p>
                        <ul className="mt-3 space-y-2">
                          {s.agreement_digest.items.map((d) => (
                            <li key={d.agreement_id} className="text-sm" data-testid={`snapshot-pact-${d.agreement_id}`}>
                              <span className="font-display text-base">{d.child_name}</span>
                              <span className="text-[var(--ink-soft)]"> · {d.app_or_category}</span>
                              <span className="font-display-italic text-[var(--ink)] ml-2">
                                {d.days_compliant}/{d.days_total} {lang === "pt" ? "cumpridos" : "kept"}
                                {d.days_exception > 0 && ` · ${d.days_exception} ${lang === "pt" ? "excepção" : "exception"}`}
                                {d.days_broken > 0 && ` · ${d.days_broken} ${lang === "pt" ? "não cumpridos" : "broken"}`}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}
          {snapshots.length === 0 && (
            <p className="font-display-italic text-[var(--ink-soft)]" data-testid="snapshots-empty">
              {t("app.snapshotEmpty")}
            </p>
          )}

          {doneItems.length > 0 && (
            <div>
              <p className="label-cap mb-4">— {t("app.tabDone")}</p>
              <ul className="divide-y divide-[var(--border)] border-y border-[var(--border)]">
                {doneItems.map((it) => (
                  <li key={it.id} className="py-5 grid grid-cols-12 gap-4 items-baseline" data-testid={`diary-done-${it.id}`}>
                    <span className="col-span-3 label-cap text-[var(--ink-faint)]">
                      {fmt(it.completed_at || it.created_at)}
                    </span>
                    <span className="col-span-9 font-display text-lg leading-snug">
                      {it.title}
                      <span className="text-[var(--ink-faint)] ml-2 text-sm font-sans">
                        · {t(`app.domainLabels.${it.domain}`)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {inProgress.length > 0 && (
            <div>
              <p className="label-cap mb-4">— {t("app.tabActive")}</p>
              <ul className="divide-y divide-[var(--border)] border-y border-[var(--border)]">
                {inProgress.map((it) => (
                  <li key={it.id} className="py-5 grid grid-cols-12 gap-4 items-baseline">
                    <span className="col-span-3 label-cap text-[var(--ink-faint)]">{fmt(it.created_at)}</span>
                    <span className="col-span-9 font-display text-lg leading-snug">
                      {it.title}
                      <span className="text-[var(--ink-faint)] ml-2 text-sm font-sans">
                        · {t(`app.domainLabels.${it.domain}`)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <aside className="md:col-span-4">
          <div className="sticky top-12 space-y-6">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={LIFE_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="card-flat">
              <p className="label-cap">— Memória</p>
              <p className="font-display text-xl mt-3 leading-snug">Confidencial por construção.</p>
              <p className="text-sm text-[var(--ink-soft)] mt-3 leading-relaxed">
                O conteúdo do diário não alimenta modelos. Não sai do perímetro da sua conta. Não é acedido excepto em escalada explícita consentida.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
